import { CommonModule } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from "@angular/core";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject } from "rxjs";
import { CaptionComponent } from "src/app/components/default/caption/caption.component";
import { CaptionContentPart } from "src/app/components/default/caption/caption.interface";
import { ChartComponent } from "src/app/components/default/chart/chart.component";
import { ChartContentPart } from "src/app/components/default/chart/chart.interface";
import { FormComponent } from "src/app/components/default/form/form.component";
import { Mode } from "src/app/components/default/form/form.definition";
import { FormContentPart, GridLayout } from "src/app/components/default/form/form.interface";
import { MenuService } from "src/app/components/default/menu/menu.service";
import { TableComponent } from "src/app/components/default/table/table.component";
import { TABLE_TYPES, TableContentPart } from "src/app/components/default/table/table.interface";
import { TileComponent } from "src/app/components/default/tile/tile.component";
import { TileContentPart } from "src/app/components/default/tile/tile.interface";
import { DialogService, InstanceData } from "src/app/components/global/dialog/dialog.service";
import { ContentDialogData } from "src/app/components/global/dialog/impl/content-dialog/content-dialog.component";
import { ErrorMessageComponent } from "src/app/components/global/snackbar/impl/error-message/error-message.component";
import { SnackbarService } from "src/app/components/global/snackbar/snackbar.service";
import { DefaultComponent } from "src/app/default.component";
import { JsonUtils } from "src/classes/JsonUtils";
import { ROUTES_CONFIG } from "src/config/routes.config";
import { Layout } from "src/enums/layout";
import { ENVIRONMENT } from "src/environments/environment";
import { Action, Message } from "src/interfaces/post-request/post-request";
import { ApplicationService } from "src/services/application.service";
import { HttpService } from "src/services/http.service";
import { SessionService } from "src/services/session.service";
import { BookmarkScreenComponent } from "../bookmarks/bookmarks.component";
import { ContentCaption } from "./impl/content-caption";
import { ContentChart } from "./impl/content-chart";
import { ContentForm } from "./impl/content-form";
import { ContentTable } from "./impl/content-table";

export interface FFWDContent {
  content: {
    title: string;
    devinfo: string;
    coachcontext?: string;
    contentparts: ContentPartResponse[];
    id: string;
    context?: { id: string; value: string }[];
    gridlayout: string;
    redirecthome?: string;
  };
  result?: string;
  messages?: Message[];
}

export type ContentResponse = { content: string; refreshSession: string; sto?: string }[];
export type ContentPartType = "INVALID" | "Table" | "Form" | "Chart" | "Timeline" | "Caption" | "Tile" | "Map";
export type ContentPartResponse = FormContentPart | TableContentPart | ChartContentPart | CaptionContentPart | TileContentPart;
export type ContentPart = ContentForm | ContentTable | ContentChart | ContentCaption;
export type ComponentSize = "full" | "auto";

@Component({
  selector: "app-content",
  imports: [
    CommonModule,
    FormComponent,
    TableComponent,
    ChartComponent,
    CaptionComponent,
    TileComponent,
    MatProgressSpinnerModule,
    BookmarkScreenComponent,
  ],
  templateUrl: "./content.component.html",
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ["./content.component.less", "./content.print.less"],
})
export class ContentScreenComponent extends DefaultComponent implements OnInit, OnDestroy, AfterViewInit, OnChanges {
  public application: ApplicationService;
  public route: ActivatedRoute;
  private router: Router;
  private http: HttpService;
  public session: SessionService;
  private translate: TranslateService;
  private menu: MenuService;
  private snackbar: SnackbarService;
  private dialog: DialogService;

  @Input()
  public id: string | null;

  @Input()
  public dynamicjson: string | null;

  @Input()
  public content: FFWDContent | null;

  @Input()
  public dialogRef: string | null;

  @Input()
  public showTitle: boolean;

  @Input()
  public contentPostUrl: string;

  @ViewChildren(TableComponent)
  public tables: QueryList<TableComponent> | null;

  @ViewChildren("component")
  public componentRefs: QueryList<ElementRef<HTMLElement>>;

  @Output()
  public loading: BehaviorSubject<boolean>;

  public components: Map<number, ContentPart>;

  public fullsize: boolean;
  public isGrid: boolean;
  public hasCharts: boolean;

  public constructor() {
    super();
    this.application = inject(ApplicationService);
    this.route = inject(ActivatedRoute);
    this.router = inject(Router);
    this.http = inject(HttpService);
    this.session = inject(SessionService);
    this.translate = inject(TranslateService);
    this.menu = inject(MenuService);
    this.snackbar = inject(SnackbarService);
    this.dialog = inject(DialogService);
    this.dialogRef = null;
    this.contentPostUrl = ROUTES_CONFIG.contentUrl;

    this.tables = null;
    this.componentRefs = new QueryList();
    this.loading = new BehaviorSubject(false);

    this.id = null;
    this.dynamicjson = null;
    this.content = null;

    this.fullsize = false;
    this.isGrid = false;
    this.hasCharts = false;
    this.showTitle = false;

    this.components = new Map<number, ContentPart>();
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if ("id" in changes && !changes["id"].firstChange && this.id) this.load(this.id, true, this.dynamicjson ?? undefined);
  }

  public ngOnInit(): void {
    if (this.id) {
      this.load(this.id, true, this.dynamicjson ?? undefined);
    } else if (!this.content) {
      this.addSubscription(
        this.route.params.subscribe((params: Params) => {
          const id = params["id"];
          const dynamicjson = this.router.lastSuccessfulNavigation()?.extras.state?.["dynamicjson"];
          this.load(id, undefined, dynamicjson ? JSON.stringify(dynamicjson) : undefined);
        }),
      );
    } else {
      this.load("");
    }
  }

  public ngAfterViewInit(): void {
    this.addSubscription(
      this.componentRefs.changes.subscribe((list: QueryList<ElementRef<HTMLElement>>) => {
        if (this.components.size == list.length) {
          const first = list.get(0);
          if (first) {
            first.nativeElement.focus();
          }
        }
      }),
    );
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    for (const component of this.components.values()) component.deconstruct();
  }

  public async load(id: string, modal?: boolean, dynamicjson?: string): Promise<void> {
    let response: ContentResponse | null = null;
    let data: FFWDContent | null = null;
    let pagenr: number | null = null;

    try {
      if (!modal && this.application.splitscreen.value?.type !== "copy") this.application.loading.next(true);

      if (this.route.snapshot.queryParams["page"]) {
        if (this.route.snapshot.queryParams["page"]) {
          const _pagenr = parseInt(this.route.snapshot.queryParams["page"]);
          if (!isNaN(_pagenr)) {
            pagenr = _pagenr - 1; // 0 based
          }
        }
      }

      this.loading.next(true);
      this.id = id;
      this.components.clear();

      if (this.content) {
        data = this.content;
      } else {
        const params: Record<string, unknown> = {
          ID: id,
          modal: modal ?? null,
          dynamicjson: dynamicjson ?? null,
          pagenr,
        };
        response = await this.http.retrieve<ContentResponse>(ROUTES_CONFIG.contentUrl, params);
        if (this.id !== id) {
          // User switched pages during http request, cancel fullfillment
          return;
        }
        if (response[0].sto && this.session.isInvalid(JsonUtils.tryParse(response[0].sto))) {
          const result = await this.session.handleSessionEvent(JsonUtils.tryParse(response[0].sto));
          if (result) {
            return this.load(id, modal, dynamicjson);
          } else {
            this.application.loading.next(false);
            this.loading.next(false);
            return;
          }
        }
        if (response[0].refreshSession.length) await this.session.update(JsonUtils.tryParse(response[0].refreshSession));
        data = JsonUtils.tryParse(response[0].content);
        if (data?.messages?.length) {
          this.application.onMessage(data.messages);
        }
        if (data?.result === "fail") {
          console.error(`Unable to load content[${id}]`, response);
          this.application.loading.next(false);
          this.loading.next(false);
          this.router.navigate(["app/home"]);
          return;
        }
      }

      if (data) {
        const contentid = data.content.id;
        const title = data.content.title.replace("_", " ");

        if (data.content.redirecthome && id === "home") {
          this.application.refresh(`/${data.content.redirecthome}`);
          return;
        }

        if (!modal) {
          this.application.title.next(title);
        }
        this.isGrid = this.application.layout == Layout.DESKTOP && data.content.gridlayout == "true";
        this.application.debugContext.next(data.content.devinfo);
        this.application.context.next(data.content.context ?? []);
        this.menu.checklist.next(data.content.coachcontext || null);

        this.addSubscription(
          this.application.layoutChanged.subscribe(
            () => (this.isGrid = this.application.layout == Layout.DESKTOP && data != null && data.content.gridlayout == "true"),
          ),
        );

        if (!this.content && !this.application.index) throw new Error("Invalid tabindex in ContentComponent");

        for (const [index, part] of data.content.contentparts.entries()) {
          let component: ContentPart | null = null;
          switch (part.type) {
            case "table": {
              component = this.createTable(contentid, <TableContentPart>part);
              break;
            }
            case "form": {
              component = ContentScreenComponent.createForm(
                contentid,
                <FormContentPart>part,
                this.http,
                this.application,
                this.session,
                this.translate,
                this.dialog,
                this.contentPostUrl,
                this.showTitle,
                this.dialogRef,
              );
              break;
            }
            case "graph":
              component = this.createChart(contentid, <ChartContentPart>part);
              break;

            case "caption":
              component = this.createCaption(contentid, <CaptionContentPart>part);
              break;
          }

          if (component) {
            component.title = component.title || data.content.title;
            component.gridStyle =
              this.isGrid && this.isGridStyle((<FormContentPart>part).gridlayout)
                ? component.parseGridStyle((<FormContentPart>part).gridlayout)
                : null;
            this.components.set(index, component);
          } else {
            throw new Error(`Could not find component: ${part.type}`);
          }
        }

        if (ENVIRONMENT.DEBUG)
          console.warn("[CONTENT] Loading => ", {
            id: id,
            components: this.components.values(),
            info: data.content.devinfo,
            data,
          });

        this.fullsize = !!Array.from(this.components.values()).filter((component, index) => {
          if (index != 0 && index != this.components.size - 1) component.size = "auto";
          return component.size == "full";
        }).length;

        this.hasCharts = Array.from(this.components.values()).some((x) => x.type === "Chart");
        this.application.loading.next(false);
        this.loading.next(false);
      } else {
        throw new Error("Invalid Data:FFWDContent");
      }
    } catch (err) {
      console.error(`Unable to load content[${id}]`, err, response);
      this.application.loading.next(false);
      this.loading.next(false);
      this.router.navigate(["app/home"]);
      this.snackbar.open<string>(ErrorMessageComponent, "DIALOG.ERRORS.INVALIDCONTENT");
    }
  }

  public sendAction(id: string, data: string): Promise<unknown> {
    return this.http.send<Action>(ROUTES_CONFIG.actionurl, {
      actionData: data,
      FFWDActionID: id,
    });
  }

  public createTable(contentid: string, data: TableContentPart): ContentTable {
    const component = new ContentTable(data.id, this.http, this.application, undefined, this.session, this.dialog, this.contentPostUrl);
    component.tabindex = this.application.index;
    component.contentid = this.id || contentid || "";
    component.contentpartid = data.id.split("ID")[1];
    component.activeFilters = data.groupfilters;
    component.headers = component.parseHeaders(data);
    component.root = component.parse(data.data);
    component.statusactionid = data.setstatusaction;
    component.searchactionid = data.setsearchfiltersaction;
    component.sortactionid = data.setcolumnsortaction;
    component.searchkeys = data.searchfilter?.map((key) => key.filter) || [];
    component.buttons = data.buttons;
    component.options = {
      archive: false,
      add: false,
      search: true,
      filter: component.root.groups.length > 0,
      selectable: true,
      delete: data.candelete == "True",
      download: data.candownload == "True" || data.candelete == "True" || data.buttons.some((button) => button.id === "17"),
      type: TABLE_TYPES[data.tabletype !== undefined ? data.tabletype - 1 : 0] ?? "DEFAULT",
      foot: data.foot == "1",
      head: data.head == "1",
      map: data.layout ? data.layout.map_columns.pin !== null && data.layout.map_columns.popup.length > 0 : false,
      tile: data.layout ? data.layout.tile_columns.length > 0 : false,
      pageSize: 25,
      layout: data.layout ?? null,
      totalRows: data.nrofresults,
    };
    component.size = data.wp === "true" ? "full" : "auto";
    return component;
  }

  public static createForm(
    contentid: string,
    data: FormContentPart,
    http: HttpService,
    application: ApplicationService,
    session: SessionService,
    translate: TranslateService,
    dialog: DialogService,
    contentPostUrl: string,
    showTitle: boolean,
    dialogRef: string | null,
    isInTableEdit: boolean = false,
  ): ContentForm {
    const component = new ContentForm(data.id, http, application, session, translate, dialog, contentPostUrl);
    // If none of these buttons id's are present, make mode view
    // const editModeButtons = ["1", "2", "3", "11"];
    // component.mode = data.buttons.some(({id}) => editModeButtons.includes(id)) ? Mode.EDIT : Mode.VIEW;
    component.mode = data.buttons.length ? Mode.EDIT : Mode.VIEW;

    component.title = data.title;
    component.attachments = component.parseAttachments(data.attachments);
    component.fields = component.parseFields(data.formfields, isInTableEdit);
    component.tabindex = application.index;
    component.contentid = contentid;
    component.contentpartid = data.id.split("ID")[1];
    component.options.attachments = data.hasfiles === "true" && !data.buttons.some((b) => b.id == "1");
    component.buttons = data.buttons;
    component.size = data.wp === "true" ? "full" : "auto";
    component.buttonenabled = data.buttonenabled === "true";

    const _dialogRef = dialogRef ? (dialog.instances.get(dialogRef) as InstanceData<ContentDialogData> | undefined) : undefined;
    if (_dialogRef?.data.title) {
      component.title = _dialogRef.data.title.label;
      component.options.head = true;
      component.options.title = true;
    }

    if (showTitle) {
      component.options.title = true;
    }

    application.limitedPrint.next(data.limitedprintenabled === "true");
    return component;
  }

  public createChart(contentid: string, data: ChartContentPart): ContentChart {
    const component = new ContentChart(data.id, this.dialog, this.application, this.session, this.http, this.contentPostUrl);
    component.contentid = contentid;
    component.charts = [
      {
        rows: data.rows,
        title: data.legendatitle,
      },
    ].map((input) => component.getChart(input.title, input.rows));
    component.size = data.wp === "true" ? "full" : "auto";
    return component;
  }

  public createCaption(contentid: string, data: CaptionContentPart): ContentCaption {
    const component = new ContentCaption(data.id, this.dialog, this.application, this.session, this.http, this.contentPostUrl);
    component.contentid = contentid;
    component.size = data.wp === "true" ? "full" : "auto";
    return component;
  }

  private isGridStyle(obj: unknown): obj is GridLayout {
    if (!(typeof obj === "object" && obj !== null)) return false;

    const keys = Object.keys(obj);
    return keys.includes("row") && keys.includes("col") && keys.includes("size");
  }
}
